import { domain } from "../auth/domain";

const getAuthHeaders = () => {
  const accessToken = localStorage.getItem("accessToken");
  if (!accessToken) {
    throw new Error("Access token not found");
  }
  return {
    "Content-Type": "application/json",
    Authorization: `JWT ${accessToken}`,
  };
};

const handleResponse = async (response) => {
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ detail: response.statusText }));
    const errorMessage = errorData.detail || Object.values(errorData).flat().join(' ');
    throw new Error(errorMessage || "An unknown error occurred");
  }
  // For 204 No Content, return a success indicator
  if (response.status === 204) {
    return { success: true };
  }
  return response.json();
};

const buildQuery = (params = {}) => {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "") return;
    query.append(key, value);
  });
  return query.toString();
};

/**
 * Fetches all special contribution programs for the user's group.
 */
export const fetchPrograms = async (params = {}) => {
  const query = buildQuery(params);
  const endpoint = query
    ? `${domain}special-contributions/?${query}`
    : `${domain}special-contributions/`;
  const response = await fetch(endpoint, {
    method: "GET",
    headers: getAuthHeaders(),
  });
  return handleResponse(response);
};

/**
 * Approves or rejects a special contribution program.
 * @param {string} programId - The ID of the program to review.
 * @param {string} [rejectionReason] - The reason for rejection. If empty, the program is approved.
 */
export const approveProgram = async (programId, rejectionReason = "") => {
  // Only include rejection_reason in the payload if it's a non-empty string.
  // For approvals, rejectionReason might be `true`, so we explicitly check for string type.
  const payload =
    typeof rejectionReason === "string" && rejectionReason
      ? { rejection_reason: rejectionReason }
      : {};
  const response = await fetch(`${domain}special-contributions/${programId}/approve/`, {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify(payload),
  });
  return handleResponse(response);
};

/**
 * Fetches all participants for a specific program.
 * @param {string} programId - The ID of the program.
 */
export const fetchProgramParticipants = async (programId) => {
  const response = await fetch(`${domain}special-contributions/${programId}/participants/`, {
    method: "GET",
    headers: getAuthHeaders(),
  });
  return handleResponse(response);
};

/**
 * Fetches all active members of the group.
 * This is a placeholder; you might have a more specific endpoint.
 * We'll use the one from UserManagement for now.
 */


/**
 * Creates a new special contribution program.
 * @param {object} payload - The program data.
 * @param {string} payload.title
 * @param {string} payload.description
 * @param {number} payload.target_per_member
 * @param {string} payload.start_date - "YYYY-MM-DD"
 * @param {string} payload.end_date - "YYYY-MM-DD"
 * @param {number[]} payload.participant_ids
 */
export const createProgram = async (payload) => {
  const response = await fetch(`${domain}special-contributions/`, {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify(payload),
  });
  return handleResponse(response);
};

/**
 * Approves or rejects a special contribution withdrawal request.
 * @param {string} withdrawalId - The ID of the withdrawal request to review.
 * @param {string} [rejectionReason] - The reason for rejection. If empty, the withdrawal is approved.
 */
export const approveWithdrawal = async (withdrawalId, rejectionReason = "") => {
  const payload =
    typeof rejectionReason === "string" && rejectionReason
      ? { rejection_reason: rejectionReason }
      : {};
  const response = await fetch(`${domain}special-contributions/withdrawals/${withdrawalId}/approve/`, {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify(payload),
  });
  return handleResponse(response);
};

/**
 * Creates a new withdrawal request for a closed program.
 * @param {object} payload - The withdrawal data.
 * @param {number} payload.program - The ID of the program.
 * @param {number} payload.amount - The amount to withdraw.
 * @param {string} payload.beneficiary_name
 * @param {string} payload.beneficiary_account
 * @param {string} [payload.purpose]
 */
export const createWithdrawalRequest = async (payload) => {
  const response = await fetch(`${domain}special-contributions/withdrawals/`, {
    method: "POST",
    headers: getAuthHeaders(),
    body: JSON.stringify(payload),
  });
  return handleResponse(response);
};
